import { FormProvider, useForm } from "react-hook-form"
import { TasksFormData, tasksSchema } from "@pma/utils/work-diaries-schema"
import { TaskForm } from "./task-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { DictType } from "@pma/types"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import axios from "axios"
import {
  CREATE_TASK_ENDPOINT,
  EDIT_TASK_ENDPOINT,
  SERVER_URL,
  TASK_DELETE_ENDPOINT,
} from "@pma/utils/api-routes"
import { useQueryClient } from "@tanstack/react-query"
import useTranslator from "@pma/hooks/use-translator"
import { toast } from "@pma/hooks/use-toast"
import { useState } from "react"
import { Button } from "../../button"

interface TaskStepProps {
  workDiaries: DictType | undefined
  project?: DictType
  setActiveStep: React.Dispatch<React.SetStateAction<number>>
}

export function TaskStep({ workDiaries, project, setActiveStep }: TaskStepProps) {
  const { translate } = useTranslator()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<TasksFormData>({
    resolver: zodResolver(tasksSchema(translate)),
    defaultValues: {
      tasks: workDiaries?._tasks.map((objTask: any) => {
        return {
          id: objTask.id,
          description: objTask.description,
          assignedTo: objTask.responsible_id,
          completed: objTask.finish_to ? true : false,
        }
      }),
    },
  })

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const queryClient = useQueryClient()

  const deleteTask = async (task_id: number | undefined) => {
    return axios.delete(`${SERVER_URL}${TASK_DELETE_ENDPOINT}/${task_id}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    })
  }

  const onSubmit = async (data: TasksFormData) => {
    setIsSubmitting(true)
    const header = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    }

    const buildTaskPayload = (objTask: any) => ({
      title: "string",
      project_id: project?.id,
      description: objTask.description,
      finish_to: objTask.completed ? 1 : 0,
      responsible_id: objTask.assignedTo,
      diary_id: workDiaries?.id,
    })

    for (const objTask of data.tasks) {
      try {
        if (objTask.actionType === "add") {
          await axios.post(
            `${SERVER_URL}${CREATE_TASK_ENDPOINT}`,
            buildTaskPayload(objTask),
            header,
          )
        } else if (objTask.actionType === "delete") {
          await deleteTask(objTask.id)
        } else if (objTask.id) {
          await axios.post(
            `${SERVER_URL}${EDIT_TASK_ENDPOINT}`,
            { ...buildTaskPayload(objTask), task_id: objTask.id },
            header,
          )
        }

        toast({
          title: translate("Success"),
          description: translate("tasks updated successfully"),
          variant: "success",
        })
        setIsSubmitting(false)
      } catch (error: any) {
        setIsSubmitting(false)
        const message = error?.response?.data?.message
        toast({
          title: translate("Error"),
          description: message,
          variant: "error",
        })
      }
    }

    await queryClient.invalidateQueries({ queryKey: ["get-work-diary-details"] })
    await queryClient.invalidateQueries({ queryKey: ["work-diaries-context"] })
    setActiveStep(3)
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
        <TaskForm />
        <div className='flex flex-col justify-between gap-4 border-t pt-6 sm:flex-row'>
          <Button
            type='button'
            variant='outline'
            className='w-full sm:w-auto'
            onClick={() => setActiveStep(1)}
          >
            {translate("Previous")}
          </Button>
          <Button
            type='submit'
            className='w-full sm:w-auto'
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {`${translate("Save")} & ${translate("Next")}`}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
